import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, GridApi, IRowNode, RowDataUpdatedEvent } from 'ag-grid-community';

import { Box, Grid } from '@mui/material';

import { StepperEnum, useWizardStepperContext } from '../../../../contexts/useWizardStepper.context';
import AgGridService from '../../../../../../../shared/services/ag-grid/ag-grid.service';
import IColumnAndFunction from '../../../../../../../shared/contracts/IColumnAndFunction';
import useWizardReportFieldsService from './wizard-report-fields.service';
import useWizardReportDataContext from '../../../../contexts/useWizardReportData.context';
import { useGetAvailableColumnsApi } from '../../../../api/useGetAvailableColumnsApi.hook';
import GridAddRowButtonOverlayComponent from './grid-add-row-button-overlay.component';
import CallOutComponent from '../../../callout/callout.component';



/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
export default function WizardReportFieldsComponent({ }: {}) {

    const { activeStep, activeStepIndex, validateStep } = useWizardStepperContext();
    const { wizardData, availableColumns, availableDataSources } = useWizardReportDataContext();
    const { setDataSourceViewNames, availableColumnsProgressComponent } = useGetAvailableColumnsApi();
    const { getColDefs, insertRow, initializeRows, updateWizardDataWithValidData } = useWizardReportFieldsService();

    const { getDefaultColDef, getDefaultProps } = new AgGridService();
    const [gridApi, setGridApi] = useState<GridApi<IColumnAndFunction>>();

    const [validRowData, setValidRowData] = useState<IColumnAndFunction[]>([]);
    const [invalidRowNodes, setInvalidRowNodes] = useState<IRowNode<IColumnAndFunction>[]>([]);

    useEffect(() => {
        if (activeStep === StepperEnum.Fields) {
            if (gridApi && validRowData?.length > 0 && invalidRowNodes.length <= 0) {
                validateStep(activeStepIndex, true)
            } else {
                validateStep(activeStepIndex, false);
            }
        }
    }, [activeStep, validRowData, invalidRowNodes])

    useEffect(() => {
        // trigger-available-columns fetch for the selected datasources
        setDataSourceViewNames(availableDataSources.filter(
            x => wizardData.datasourceGuids.includes(x.dataSourceCode)
        ).map(x => x.dataSourceViewName));
    }, [availableDataSources, wizardData.datasourceGuids]);


    useEffect(() => {
        // set default rows as valid rows
        setValidRowData(
            initializeRows(
                gridApi,
                wizardData.reportSpecification.columnSelections
            )
        );
    }, [gridApi, availableColumns,
        wizardData.reportSpecification.columnSelections
    ]);

    const handleDataChange = (e:
        CellValueChangedEvent<IColumnAndFunction, any> |
        RowDataUpdatedEvent<IColumnAndFunction>
    ) => {
        const validRows: IColumnAndFunction[] = [];
        const invalidRows: IRowNode<IColumnAndFunction>[] = [];

        const nodes = e.api.getRenderedNodes();
        nodes.forEach(node => {
            if (node.data?.field && node.data?.field !== '-1') {
                validRows.push({ ...node.data });
            } else {
                invalidRows.push(node);
            }
        });

        // ---
        setInvalidRowNodes(invalidRows);
        setValidRowData(validRows);
        // -- update the WizardData
        if (availableColumns?.length > 0) {
            updateWizardDataWithValidData(validRows);
        }
    }


    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            onAddClick: () => insertRow(gridApi, 0)
        };
    }, [gridApi]);

    return (<Box hidden={activeStep !== StepperEnum.Fields} sx={{ px: 4, pt: 1, pb: 1 }}>

        {/* loader */}
        {availableColumnsProgressComponent()}

        <Grid item container direction="column" wrap="nowrap" sx={{ p: 0 }}>

            <Grid container direction={'column'} justifyItems='center' paddingBottom={4} sx={{ p: 1 }}>
                <CallOutComponent type={'warning'} text={`
                    Note: If you change or remove the original data source in previous step, this grid will be cleared. To restore, reset or enter manually.
                `} />
            </Grid>

            {/* AgGrid */}
            <Grid container direction="row" wrap="nowrap" paddingBottom={4} sx={{ p: 0 }}>
                {/* The AG Grid component */}
                <div className={"ag-theme-quartz"}
                    style={{ height: "480px", width: "100%" }}
                >
                    <AgGridReact<IColumnAndFunction>
                        columnDefs={getColDefs}
                        defaultColDef={getDefaultColDef()}
                        {...getDefaultProps({
                            rowHeight: 44,
                            hideSideBar: true,
                            rowDragManaged: true,
                        })}
                        // for add-new-button
                        noRowsOverlayComponent={GridAddRowButtonOverlayComponent}
                        noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                        onGridReady={(e) => setGridApi(e.api)}
                        onRowDataUpdated={handleDataChange}
                    />
                </div>
            </Grid>



        </Grid>



    </Box >
    );
}